import { Autocomplete, Box, Button, LinearProgress, Switch, TextField, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { isCommonResponseSuccessfulV2 } from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithoutParams, getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'

const ASSESSMENT_LISTS_OPTIONS_URL = '/api/secure/cc/support/assessment-lists/options/lists?includeDisabled=false'

function parseUrlParams(apiSignature) {
  const urlParts = apiSignature.split('?')
  const pathname = urlParts[0]
  const params = {}
  if (urlParts.length > 1) {
    const searchParams = new URLSearchParams(urlParts[1])
    searchParams.forEach((value, key) => {
      params[key] = value
    })
  }
  return { pathname, params }
}

function getDetailsApiSignature(getApiSignature) {
  if (!getApiSignature) return null
  const urlParts = getApiSignature.split('?')
  const pathname = urlParts[0]
  const detailsPath = pathname.replace(/\/assessment-access\/?$/, '/assessment-access/details')
  return urlParts.length > 1 ? `${detailsPath}?${urlParts[1]}` : detailsPath
}

function parseAssessmentAccessResponse(response) {
  // API wraps payload in response.data.data (e.g. { code, message, data: { allowedToAssess, assessmentListAllowed, assessmentLists, assessmentAccess } })
  const payload = response?.data?.data ?? response?.data
  if (!payload) return null
  const access = payload?.assessmentAccess ?? {}
  return {
    allowedToAssess: access?.allowedToAssess ?? payload?.allowedToAssess ?? false,
    assessmentListAllowed: access?.assessmentListAllowed ?? payload?.assessmentListAllowed ?? [],
    assessmentLists: access?.assessmentLists ?? payload?.assessmentLists ?? [],
  }
}

/** Returns refs that have no matching assessment in the assessments array (for "assessment missing / deleted" warning). */
function getUnresolvedAssessmentRefs(assessmentRefs, assessments) {
  if (!Array.isArray(assessmentRefs) || assessmentRefs.length === 0) return []
  const list = Array.isArray(assessments) ? assessments : []
  return assessmentRefs.filter((ref) => {
    const value = ref?.value
    if (value == null) return true
    const isId = ref?.type === 'id'
    return !list.some(
      (a) => (isId ? a?.id === value : (a?.slug === value || a?.id === value)),
    )
  })
}

export default function AssessmentAccessSettings({
  assessmentAccessGetApiSignature,
  assessmentAccessSaveApiSignature,
}) {
  const [loading, setLoading] = useState(true)
  const [saveLoading, setSaveLoading] = useState(false)
  const [allowedToAssess, setAllowedToAssess] = useState(false)
  const [assessmentListAllowed, setAssessmentListAllowed] = useState([])
  const [assessmentLists, setAssessmentLists] = useState([])
  const [listOptions, setListOptions] = useState([])
  const [optionsLoading, setOptionsLoading] = useState(true)

  const fetchAssessmentAccessDetails = useCallback(async () => {
    const detailsSignature = getDetailsApiSignature(assessmentAccessGetApiSignature)
    const urlToUse = detailsSignature ?? assessmentAccessGetApiSignature
    if (!urlToUse) return
    setLoading(true)
    const { pathname, params } = parseUrlParams(urlToUse)
    const response = await getSPDataWithParams(pathname, params)
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const parsed = parseAssessmentAccessResponse(response)
      if (parsed) {
        setAllowedToAssess(!!parsed.allowedToAssess)
        setAssessmentListAllowed(Array.isArray(parsed.assessmentListAllowed) ? parsed.assessmentListAllowed : [])
        setAssessmentLists(Array.isArray(parsed.assessmentLists) ? parsed.assessmentLists : [])
      }
    }
    setLoading(false)
  }, [assessmentAccessGetApiSignature])

  const fetchListOptions = useCallback(async () => {
    setOptionsLoading(true)
    const response = await getSPDataWithoutParams(ASSESSMENT_LISTS_OPTIONS_URL)
    if (response?.status === 200) {
      const raw = response?.data?.data ?? response?.data
      const list = Array.isArray(raw) ? raw : []
      setListOptions(list.filter((item) => item?.enabled !== false))
    } else {
      setListOptions([])
    }
    setOptionsLoading(false)
  }, [])

  useEffect(() => {
    fetchAssessmentAccessDetails()
  }, [fetchAssessmentAccessDetails])

  useEffect(() => {
    fetchListOptions()
  }, [fetchListOptions])

  async function saveAssessmentAccess() {
    if (!assessmentAccessSaveApiSignature) return
    setSaveLoading(true)
    const { pathname, params } = parseUrlParams(assessmentAccessSaveApiSignature)
    const body = {
      allowedToAssess,
      assessmentListAllowed: assessmentListAllowed.map((ref) =>
        ref?.type ? ref : { type: 'id', value: typeof ref === 'string' ? ref : ref?.value },
      ),
    }
    const response = await postSPDataWithParams(pathname, body, params)
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const parsed = parseAssessmentAccessResponse(response)
      if (parsed) {
        setAllowedToAssess(!!parsed.allowedToAssess)
        setAssessmentListAllowed(Array.isArray(parsed.assessmentListAllowed) ? parsed.assessmentListAllowed : [])
      }
      // Refetch details so we have updated assessmentLists (titles + assessments)
      await fetchAssessmentAccessDetails()
      toast.success('Assessment access settings saved successfully')
    } else {
      toast.error('Failed to save assessment access settings')
    }
    setSaveLoading(false)
  }

  const selectedIds = assessmentListAllowed.map((ref) => (ref?.value != null ? ref.value : ref))
  // Resolve selected options from listOptions; fall back to assessmentLists (from details) so selection shows even before options load
  const selectedOptions = selectedIds
    .map((id) => listOptions.find((o) => o?.id === id) ?? assessmentLists.find((l) => l?.id === id))
    .filter(Boolean)

  return (
    <div style={{ width: '100%', maxWidth: '768px', paddingBottom: '80px' }}>
      {loading && <LinearProgress />}

      <Typography variant="h6" className="mb-3 fw-bold">
        Assessment Access
      </Typography>
      <Typography variant="body2" color="text.secondary" className="mb-3">
        Control whether this practitioner can run assessments and which assessment lists they can use in session.
      </Typography>

      <Box className="mb-4">
        <div className="d-flex justify-content-between align-items-center py-2">
          <div className="d-flex flex-column gap-1">
            <div className="fw-medium">Allowed to run assessments</div>
            <div className="text-muted small">
              When off, the practitioner cannot run assessments even if lists are assigned.
            </div>
          </div>
          <Switch
            id="allowedToAssess"
            checked={allowedToAssess}
            onChange={(e) => setAllowedToAssess(e.target.checked)}
          />
        </div>
      </Box>

      <Box className="mb-4">
        <Typography variant="subtitle2" className="mb-2">
          Assessment lists
        </Typography>
        {!allowedToAssess && (
          <Typography variant="caption" color="text.secondary" className="d-block mb-2">
            Enable &quot;Allowed to run assessments&quot; to assign lists.
          </Typography>
        )}
        <Autocomplete
          multiple
          disabled={!allowedToAssess || optionsLoading}
          options={listOptions}
          getOptionLabel={(option) => option?.title ?? option?.slug ?? option?.id ?? ''}
          value={selectedOptions}
          onChange={(_, newValue) => {
            setAssessmentListAllowed(
              (newValue || []).map((item) => ({ type: 'id', value: item?.id ?? item })),
            )
          }}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder={optionsLoading ? 'Loading lists…' : 'Select assessment lists'}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.title || option.slug || option.id}
            </li>
          )}
        />
        {allowedToAssess && assessmentListAllowed.length === 0 && (
          <Typography variant="caption" color="warning.main" className="d-block mt-1">
            No assessment lists assigned. Assign at least one list for the practitioner to see assessments.
          </Typography>
        )}
      </Box>

      {!loading && assessmentLists.length > 0 && (
        <Box className="mb-4">
          <Typography variant="subtitle2" className="mb-2 fw-bold">
            Allowed lists and their assessments
          </Typography>
          {assessmentLists.map((list) => {
            const unresolvedRefs = getUnresolvedAssessmentRefs(
              list?.assessmentRefs,
              list?.assessments,
            )
            return (
              <Box
                key={list.id}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  mb: 1.5,
                }}
              >
                <Typography variant="subtitle2" className="fw-medium mb-1">
                  {list.title ?? list.slug ?? list.id}
                </Typography>
                {Array.isArray(list.assessments) && list.assessments.length > 0 ? (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, fontSize: '0.875rem', color: 'text.secondary' }}>
                    {list.assessments.map((a) => (
                      <li key={a.id ?? a.slug}>
                        {a.title ?? a.slug ?? a.id}
                      </li>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No assessments in this list.
                  </Typography>
                )}
                {unresolvedRefs.length > 0 && (
                  <Typography variant="caption" color="warning.main" className="d-block mt-1">
                    Assessment missing / deleted: {unresolvedRefs.map((r) => r?.title ?? r?.value).filter(Boolean).join(', ')}
                  </Typography>
                )}
              </Box>
            )
          })}
        </Box>
      )}

      <div className="d-flex" style={{ justifyContent: 'flex-end' }}>
        {!loading && (
          <Button
            onClick={saveAssessmentAccess}
            variant="contained"
            disabled={saveLoading || loading}
          >
            Save {saveLoading && <Spinner animation="border" variant="light" size="sm" />}
          </Button>
        )}
      </div>
    </div>
  )
}
